import { Search } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { useSaveProject, useSetProjectMembers, useUsers, type Project, type Role } from "@/api/queries";
import { Field } from "@/components/common/Field";
import { UserAvatar } from "@/components/common/UserAvatar";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { errorText, fieldError } from "@/lib/errors";

import { defaultColor, hoursToMinutes, minutesToHours, palette } from "./lib";

type ProjectDialogProps = {
  project?: Project;
  onClose: () => void;
  onSaved?: (project: Project) => void;
};

/** Creates or edits a project: name, description, color and budget. */
export function ProjectDialog({ project, onClose, onSaved }: ProjectDialogProps) {
  const { t } = useTranslation();
  const save = useSaveProject();
  const [name, setName] = useState(project?.name ?? "");
  const [description, setDescription] = useState(project?.description ?? "");
  const [color, setColor] = useState(project?.color ?? defaultColor);
  const [budget, setBudget] = useState(minutesToHours(project?.budget_minutes));
  const budgetMinutes = hoursToMinutes(budget);
  const budgetInvalid = Number.isNaN(budgetMinutes);
  const colors = palette.includes(color) ? palette : [...palette, color];

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (budgetInvalid) return;
    try {
      const saved = await save.mutateAsync({
        projectId: project?.id,
        body: { name: name.trim(), description: description.trim(), color, budget_minutes: budgetMinutes },
      });
      onSaved?.(saved);
      onClose();
    } catch {
      // Shown below.
    }
  };

  const nameError = fieldError(t, save.error, "name");
  const budgetError = budgetInvalid ? t("fieldErrors.invalid") : fieldError(t, save.error, "budget_minutes");

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title={project ? t("projects.editTitle", { name: project.name }) : t("projects.create")}>
        <form className="flex flex-col gap-4 p-5" onSubmit={(e) => void submit(e)}>
          <Field label={t("projects.name")} error={nameError}>
            <Input
              required
              autoFocus
              maxLength={120}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Field label={`${t("projects.description")} (${t("common.optional")})`}>
            <textarea
              rows={3}
              maxLength={2000}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded-lg border bg-surface px-3 py-2 shadow-card"
            />
          </Field>
          <fieldset className="flex flex-col gap-1.5">
            <legend className="mb-1.5 text-sm font-medium">{t("projects.color")}</legend>
            <div className="flex flex-wrap gap-2">
              {colors.map((c) => (
                <label key={c} className="cursor-pointer">
                  <input
                    type="radio"
                    name="color"
                    value={c}
                    checked={color === c}
                    onChange={() => setColor(c)}
                    className="peer sr-only"
                  />
                  <span
                    className="block size-7 rounded-full ring-offset-2 ring-offset-surface peer-checked:ring-2 peer-checked:ring-fg peer-focus-visible:ring-2 peer-focus-visible:ring-ring"
                    style={{ backgroundColor: c }}
                  />
                  <span className="sr-only">{c}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <Field label={t("projects.budgetHours")} hint={t("projects.budgetHint")} error={budgetError}>
            <Input
              inputMode="decimal"
              className="max-w-40"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
            />
          </Field>
          {save.error && !nameError && !budgetError && (
            <Alert variant="danger">{errorText(t, save.error)}</Alert>
          )}
          <div className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                {t("common.cancel")}
              </Button>
            </DialogClose>
            <Button type="submit" disabled={save.isPending || !name.trim() || budgetInvalid}>
              {project ? t("common.save") : t("common.create")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

type Candidate = { id: string; name: string; hint: string; role?: Role };

/**
 * Replaces the members and managers of a project. Managers can add only the
 * people they already see; current members are always listed.
 */
export function ProjectMembersDialog({ project, onClose }: { project: Project; onClose: () => void }) {
  const { t } = useTranslation();
  const users = useUsers();
  const setMembers = useSetProjectMembers();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Map<string, boolean>>(
    () => new Map(project.members.map((m) => [m.user_id, m.is_manager])),
  );

  const known = new Map((users.data ?? []).map((u) => [u.id, u]));
  const candidates: Candidate[] = [
    ...(users.data ?? [])
      .filter((u) => u.status !== "blocked" || selected.has(u.id))
      .map((u) => ({ id: u.id, name: u.full_name, hint: t(`roles.${u.role}`), role: u.role })),
    ...project.members
      .filter((m) => !known.has(m.user_id))
      .map((m) => ({ id: m.user_id, name: m.full_name, hint: t("projects.roleUnknown") })),
  ];
  const q = query.trim().toLowerCase();
  const visible = candidates.filter(
    (c) =>
      !q || c.name.toLowerCase().includes(q) || (known.get(c.id)?.email.toLowerCase().includes(q) ?? false),
  );

  const update = (id: string, value: boolean | null) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (value === null) next.delete(id);
      else next.set(id, value);
      return next;
    });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await setMembers.mutateAsync({
        projectId: project.id,
        members: [...selected].map(([user_id, is_manager]) => ({ user_id, is_manager })),
      });
      onClose();
    } catch {
      // Shown below.
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title={t("projects.membersTitle", { name: project.name })}
        description={t("projects.membersDialogHint")}
        className="w-[min(92vw,600px)]"
      >
        <form className="flex flex-col gap-4 p-5" onSubmit={(e) => void submit(e)}>
          <div className="relative">
            <Search
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted"
              aria-hidden="true"
            />
            <Input
              type="search"
              aria-label={t("projects.membersFilter")}
              placeholder={t("projects.membersFilter")}
              className="pl-9"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <ul className="flex max-h-[50dvh] flex-col gap-0.5 overflow-y-auto">
            {visible.length === 0 && <li className="px-2 py-3 text-muted">{t("common.noResults")}</li>}
            {visible.map((c) => {
              const member = selected.has(c.id);
              const isManager = selected.get(c.id) ?? false;
              // Unknown roles: the server decides; the current flag can only be kept or removed.
              const canManage = c.role ? c.role !== "employee" : isManager;
              return (
                <li
                  key={c.id}
                  className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-surface-muted"
                >
                  <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                    <input
                      type="checkbox"
                      checked={member}
                      onChange={() => update(c.id, member ? null : false)}
                      className="size-4 accent-(--primary-solid)"
                    />
                    <UserAvatar name={c.name} className="size-7" />
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{c.name}</span>
                      <span className="block truncate text-xs text-muted">{c.hint}</span>
                    </span>
                  </label>
                  {member && (
                    <label
                      className="flex items-center gap-2 text-sm"
                      title={canManage ? undefined : t("projects.managerOnlyStaff")}
                    >
                      <span className={canManage ? "" : "text-muted"}>{t("projects.manager")}</span>
                      <Switch
                        checked={isManager}
                        disabled={!canManage}
                        onCheckedChange={(v) => update(c.id, v)}
                        aria-label={`${t("projects.manager")}: ${c.name}`}
                      />
                    </label>
                  )}
                </li>
              );
            })}
          </ul>
          {setMembers.error && <Alert variant="danger">{errorText(t, setMembers.error)}</Alert>}
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm text-muted">{t("projects.membersCount", { count: selected.size })}</span>
            <div className="flex gap-2">
              <DialogClose asChild>
                <Button type="button" variant="secondary">
                  {t("common.cancel")}
                </Button>
              </DialogClose>
              <Button type="submit" disabled={setMembers.isPending}>
                {t("common.save")}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
